import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FocusScope } from "react-aria";
import { useEffect, useState } from "react";

import {
  DesktopMenuOverlaySpacer,
  StoplightSpacer,
} from "@/components/layout/components/sidebar/sidebar-desktop-section";
import { isMac } from "@/domains/platform/isMac";
import {
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSLoadingSpinner,
  MDSLogo,
  MDSLogoKind,
  MDSLogoSize,
} from "@/mds";
import { MDSZIndexes } from "@/mds/foundations";
import { clientEnvModule } from "@/modules/client-env";
import { useAgentOverlayLifecycleAnalytics } from "@/pages/agent/analytics/use-agent-overlay-analytics";
import { AgentOverlayContent } from "@/pages/agent/components/agent-overlay-content";
import { useAgentIntentGraph } from "@/pages/agent/intent-graph/use-agent-intent-graph";
import { useAppStore } from "@/store";

export const AgentOverlay = observer(() => {
  const { store } = useAppStore();
  const { agentOverlay } = store;
  const activeSurface = agentOverlay.activeSurface;
  const isOpen = agentOverlay.isOpen;
  const isDesktop = clientEnvModule.isDesktop();
  const isMacOS = isMac();
  const shouldReserveMacOSTrafficLights = isDesktop && isMacOS;
  const shouldReserveDesktopMenu = isDesktop && !isMacOS;
  const [isSurfaceReady, setIsSurfaceReady] = useState(false);
  useAgentIntentGraph({ enabled: agentOverlay.isTaskProjectTrackingEnabled });
  useAgentOverlayLifecycleAnalytics({ activeSurface, isOpen });

  useEffect(() => {
    if (!isOpen) {
      setIsSurfaceReady(false);
      return;
    }

    const frame = requestAnimationFrame(() => {
      setIsSurfaceReady(true);
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <FocusScope contain restoreFocus autoFocus>
      {/* Escape is handled here rather than on `window` so anything nested that
          dismisses on Escape — a filter dropdown, the project preview, the
          search field's clear — gets it first and stops it propagating. React
          routes synthetic events through the React tree, so this still fires for
          the portal-rendered popovers. */}
      <StyledOverlayRoot
        aria-label="Mem Agent"
        aria-modal="true"
        data-test-id="agent-overlay"
        role="dialog"
        tabIndex={-1}
        onKeyDown={event => {
          if (event.key !== "Escape" || event.defaultPrevented) return;

          event.preventDefault();
          agentOverlay.close();
        }}
      >
        <StyledHeader>
          <StyledHeaderBrand>
            {shouldReserveMacOSTrafficLights ? (
              <StoplightSpacer data-component="StoplightSpacer" />
            ) : null}
            {shouldReserveDesktopMenu ? (
              <DesktopMenuOverlaySpacer data-component="DesktopMenuOverlaySpacer" />
            ) : null}
            <StyledLogo kind={MDSLogoKind.ArrowsCropped} size={MDSLogoSize.Medium} />
          </StyledHeaderBrand>
          <MDSIconButton
            aria-label="Close agent"
            iconKind={MDSIconKind.Exit}
            size={MDSIconButtonSize.Small}
            variant={MDSIconButtonVariant.Transparent}
            onPress={() => agentOverlay.close()}
          />
        </StyledHeader>
        <StyledBody>
          {isSurfaceReady ? (
            <AgentOverlayContent activeSurface={activeSurface} />
          ) : (
            <StyledSurfaceLoading aria-label="Loading agent page">
              <MDSLoadingSpinner />
            </StyledSurfaceLoading>
          )}
        </StyledBody>
      </StyledOverlayRoot>
    </FocusScope>
  );
});

const StyledOverlayRoot = styled.div`
  background: ${({ theme }) => theme.colors.surface.onboarding};
  display: flex;
  flex-direction: column;
  inset: 0;
  outline: none;
  position: fixed;
  z-index: ${MDSZIndexes.ScreenOverlay};
`;

const StyledHeader = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[5]}`};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const StyledHeaderBrand = styled.div`
  align-items: center;
  display: flex;
`;

const StyledLogo = styled(MDSLogo)``;

const StyledBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const StyledSurfaceLoading = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
`;
